import React from 'react';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import classNames from 'classnames';

import { Icon } from '@tcc/crate';

import type { Scenario } from 'store/types/types';

import { setActiveScenarioUuid } from './index';
import { setPlantingPlanParams } from 'planting-plan-form';
import { resetValidationResults } from 'validations';
import { currencyFormatter, getMessage } from 'util/locale';
import { convertScenarioForLocale } from './util/convertScenarioForLocale';
import { formatAvgYield, formatNumberK } from 'util/formatters/numberFormatter';
import { inferUnit } from 'util/unitConversion';

import { getActiveCropNominalWeight } from 'current/currentSelectors';
import { allowDecimalByCountry } from 'user/selectors';
import { getCurrency } from 'user/userSelectors';
import { isActiveScenario, isOriginalScenario } from './scenarioSelectors';

const baseString = 'vra-scenario-density-tool';

const ScenarioForDensityTool = ({ scenario }: { scenario: Scenario }) => {
  const cropNominalWeight = useAppSelector(getActiveCropNominalWeight);
  const currency = useAppSelector(getCurrency);
  const allowDecimal = useAppSelector(allowDecimalByCountry);
  const isActive = useAppSelector((state) => isActiveScenario(state, scenario.scenarioUuid));
  const isOriginal = useAppSelector((state) => isOriginalScenario(state, scenario.scenarioUuid));
  const convertedScenario = convertScenarioForLocale({ scenario, cropNominalWeight });
  const formattedPriceCommodity = currencyFormatter(convertedScenario.plantingPlanParams['price-commodity'], currency);
  const formattedPriceProduct = currencyFormatter(convertedScenario.plantingPlanParams['price-product'], currency);
  const numZones = Object.keys(convertedScenario.zones).length;
  const formattedAvgEstYield = formatAvgYield(convertedScenario.avgEstYield.q, allowDecimal);

  const dispatch = useAppDispatch();

  const classString = classNames({
    [`${baseString}`]: true,
    isActive
  });

  const selectScenario = () => {
    dispatch(setActiveScenarioUuid(scenario.scenarioUuid));
    dispatch(setPlantingPlanParams(scenario.plantingPlanParams));
    dispatch(resetValidationResults());
  };

  const checkMark = isActive ? <Icon className={`${baseString}__check`} glyph={Icon.set.CHECK} /> : null;
  const recommendedMarker = isOriginal ? (
    <div className={`${baseString}__recommended-marker`} data-test-id='recommended-marker'>
      {getMessage('text__recommended--short')}
    </div>
  ) : null;

  return (
    <li className={classString} onClick={selectScenario}>
      <ul>
        <li>{formatNumberK(convertedScenario.avgRate.q)}</li>
        <li>{formattedAvgEstYield}</li>
        <li>{`${formattedPriceCommodity}/${inferUnit('mass.yield').symbol.quantitySpecific}`}</li>
        <li>{`${formattedPriceProduct}/${inferUnit('mass.yield').symbol.quantitySpecific}`}</li>
        <li>{getMessage('label-text__scenarios-zones', { numZones })}</li>
      </ul>
      <div>
        {recommendedMarker}
        {checkMark}
      </div>
    </li>
  );
};

export default ScenarioForDensityTool;
